import axiosInstance from './AxiosInstance';

const InvestmentService = {
  // 1. Create Investment
  createInvestment: async (investmentData) => {
    try {
      const response = await axiosInstance.post('/investments', investmentData);
      return { 
        success: response.data.success, 
        data: response.data.data, 
        message: response.data.message 
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to create investment',
        errors: error.response?.data?.errors || []
      };
    }
  },

  // 2. Get All Investments with filters
  getAllInvestments: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.companyId) queryParams.append('companyId', params.companyId);
      if (params.branchId) queryParams.append('branchId', params.branchId);
      if (params.userId) queryParams.append('userId', params.userId);
      if (params.status) queryParams.append('status', params.status);
      if (params.investmentType) queryParams.append('investmentType', params.investmentType);
      if (params.search) queryParams.append('search', params.search);
      if (params.fromDate) queryParams.append('fromDate', params.fromDate);
      if (params.toDate) queryParams.append('toDate', params.toDate);
      if (params.startDate) queryParams.append('fromDate', params.startDate);
      if (params.endDate) queryParams.append('toDate', params.endDate);

      const response = await axiosInstance.get(`/investments?${queryParams.toString()}`);
      return {
        success: response.data.success,
        data: response.data.data,
        pagination: response.data.pagination,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch investments',
        data: [],
        pagination: {}
      };
    }
  },

  // 3. Get Investment by ID
  getInvestmentById: async (id) => {
    try {
      const response = await axiosInstance.get(`/investments/${id}`);
      return { 
        success: response.data.success, 
        data: response.data.data, 
        message: response.data.message 
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch investment',
      };
    }
  },

  // 4. Get Investment Statistics
  getInvestmentStatistics: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.companyId) queryParams.append('companyId', params.companyId);
      if (params.branchId) queryParams.append('branchId', params.branchId);
      
      const url = `/investments/statistics${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await axiosInstance.get(url);
      
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch investment statistics',
        data: {
          totalInvestments: 0,
          activeInvestments: 0,
          maturedInvestments: 0,
          closedInvestments: 0,
          totalInvestedAmount: 0,
          totalActiveAmount: 0,
          totalReturnedAmount: 0,
          totalProfit: 0
        }
      };
    }
  },

  // 5. Update Investment
  updateInvestment: async (id, investmentData) => {
    try {
      const response = await axiosInstance.put(`/investments/${id}`, investmentData);
      return { 
        success: response.data.success, 
        data: response.data.data, 
        message: response.data.message 
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update investment',
        errors: error.response?.data?.errors || []
      };
    }
  },

  // 6. Add Money to Investment
  addMoneyToInvestment: async (id, transactionData) => {
    try {
      const response = await axiosInstance.post(`/investments/${id}/add-money`, transactionData);
      return { 
        success: response.data.success, 
        data: response.data.data,
        history: response.data.history,
        message: response.data.message 
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to add money to investment',
        errors: error.response?.data?.errors || []
      };
    }
  },

  // 7. Withdraw Money from Investment
  withdrawMoneyFromInvestment: async (id, transactionData) => {
    try {
      const response = await axiosInstance.post(`/investments/${id}/withdraw-money`, transactionData);
      return { 
        success: response.data.success, 
        data: response.data.data,
        history: response.data.history,
        message: response.data.message 
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to withdraw money from investment',
        errors: error.response?.data?.errors || []
      };
    }
  },

  // 8. Get Investment History
  getInvestmentHistory: async (id, params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.transactionType) queryParams.append('transactionType', params.transactionType);
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);

      const response = await axiosInstance.get(`/investments/${id}/history?${queryParams.toString()}`);
      return {
        success: response.data.success,
        data: response.data.data,
        pagination: response.data.pagination,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch investment history',
        data: [],
        pagination: {}
      };
    }
  },

  // 11. Get Investments History (global across investments)
  getInvestmentsHistory: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.companyId) queryParams.append('companyId', params.companyId);
      if (params.branchId) queryParams.append('branchId', params.branchId);
      if (params.transactionType) queryParams.append('transactionType', params.transactionType);
      if (params.search) queryParams.append('search', params.search);
      if (params.fromDate) queryParams.append('fromDate', params.fromDate);
      if (params.toDate) queryParams.append('toDate', params.toDate);

      const url = `/investments/investments-history${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await axiosInstance.get(url);
      return {
        success: response.data.success,
        data: response.data.data,
        pagination: response.data.pagination,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch investments history',
        data: [],
        pagination: {}
      };
    }
  },

  // 14. Download Investments History as XLSX
  downloadInvestmentsHistory: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.companyId) queryParams.append('companyId', params.companyId);
      if (params.branchId) queryParams.append('branchId', params.branchId);
      if (params.transactionType) queryParams.append('transactionType', params.transactionType);
      if (params.search) queryParams.append('search', params.search);
      if (params.fromDate) queryParams.append('fromDate', params.fromDate);
      if (params.toDate) queryParams.append('toDate', params.toDate);

      const url = `/investments/investments-history/download/xlsx${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await axiosInstance.get(url, {
        responseType: 'blob',
        headers: {
          Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }
      });
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  },

  // 12. Get Investments History Aggregate (day/week/month)
  getInvestmentsHistoryAggregate: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.view) queryParams.append('view', params.view);
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.companyId) queryParams.append('companyId', params.companyId);
      if (params.branchId) queryParams.append('branchId', params.branchId);
      // Accept alternate date names and prefer explicit fromDate/toDate
      if (params.fromDate) queryParams.append('fromDate', params.fromDate);
      if (params.toDate) queryParams.append('toDate', params.toDate);
      if (params.startDate) queryParams.append('fromDate', params.startDate);
      if (params.endDate) queryParams.append('toDate', params.endDate);

      const url = `/investments/investments-history/aggregate${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await axiosInstance.get(url);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch investments history aggregate',
        data: []
      };
    }
  },

  // 9. Record Investment Return (Maturity)
  recordInvestmentReturn: async (id, returnData) => {
    try {
      const response = await axiosInstance.post(`/investments/${id}/return`, returnData);
      return { 
        success: response.data.success, 
        data: response.data.data,
        history: response.data.history,
        profit: response.data.profit,
        message: response.data.message 
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to record investment return',
        errors: error.response?.data?.errors || []
      };
    }
  },

  // 10. Close Investment (Soft Delete)
  closeInvestment: async (id) => {
    try {
      const response = await axiosInstance.delete(`/investments/${id}`);
      return { 
        success: response.data.success, 
        message: response.data.message 
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to close investment'
      };
    }
  }
  ,
  // 13. Download investments as XLSX
  downloadInvestments: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.companyId) queryParams.append('companyId', params.companyId);
      if (params.branchId) queryParams.append('branchId', params.branchId);
      if (params.status) queryParams.append('status', params.status);
      if (params.investmentType) queryParams.append('investmentType', params.investmentType);
      if (params.search) queryParams.append('search', params.search);
      if (params.fromDate) queryParams.append('fromDate', params.fromDate);
      if (params.toDate) queryParams.append('toDate', params.toDate);

      const url = `/investments/download/xlsx${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await axiosInstance.get(url, {
        responseType: 'blob',
        headers: {
          Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }
      });
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  }
};

export default InvestmentService;
